import { useEffect, useState } from "react";
import type { ReportProgressData } from "@/lib/chat-types";
import { resolveFundComplianceGatewayBaseUrl } from "@/lib/fund-compliance/gateway";

const FIXED_STEPS = [
  "Parsing document structure",
  "Retrieving applicable regulations",
  "Analyzing compliance dimensions",
  "Compiling findings and recommendations",
  "Finalizing compliance report",
] as const;

function clampPct(value: number) {
  return Math.min(100, Math.max(0, value));
}

function getDisplayPct(data: ReportProgressData) {
  if (typeof data.pct === "number") return clampPct(data.pct);
  if (data.report?.status === "ready") return 100;
  if (data.report) return 80;
  return 0;
}

function getReportDownloadUrl(report: ReportProgressData["report"]) {
  if (!report) return null;
  const fileName = report.storageName || report.filename;
  if (!fileName) return null;
  return `${resolveFundComplianceGatewayBaseUrl()}/api/storage/v1/files/blob/${encodeURIComponent(fileName)}`;
}

export function ReportProgressCard({
  data,
  defaultExpanded = false,
}: {
  data: ReportProgressData;
  defaultExpanded?: boolean;
}) {
  const [collapsed, setCollapsed] = useState(!defaultExpanded);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setCollapsed(!defaultExpanded);
  }, [defaultExpanded, data.report?.filename]);

  const pctValue = getDisplayPct(data);
  const inProgress = pctValue < 100;
  const completedSteps = pctValue <= 0 ? 0 : Math.min(FIXED_STEPS.length, Math.ceil(pctValue / 20));
  const stepStates = FIXED_STEPS.map((label, index) => {
    if (index < completedSteps) return { label, status: "done" as const };
    return { label, status: "pending" as const };
  });

  async function handleDownload() {
    const downloadUrl = getReportDownloadUrl(data.report);
    if (!downloadUrl || !data.report || downloading) return;

    setDownloading(true);
    try {
      const response = await fetch(downloadUrl, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = data.report.filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="border border-[var(--color-border)] rounded-[10px] overflow-hidden my-1">
      {/* Header */}
      <button
        type="button"
        onClick={() => setCollapsed((value) => !value)}
        className="flex w-full items-center gap-2 px-3.5 py-2.5 bg-[var(--color-accent)] text-white cursor-pointer text-left"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
        </svg>
        <span className="flex-1 text-xs font-bold uppercase tracking-wide" style={{ fontFamily: "var(--font-mono)" }}>{data.title}</span>
        {inProgress && (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-70 animate-spin">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        )}
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="transition-transform"
          style={{ transform: collapsed ? "rotate(0deg)" : "rotate(180deg)" }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div
        className="grid transition-[grid-template-rows,opacity] duration-200 ease-out"
        style={{
          gridTemplateRows: collapsed ? "0fr" : "1fr",
          opacity: collapsed ? 0 : 1,
        }}
      >
        <div className="min-h-0 overflow-hidden">
          {/* Steps */}
          <div className="px-3.5 py-1.5">
            {stepStates.map((step, i) => (
              <div key={i} className="flex items-center gap-2 py-1.5" style={{ borderBottom: i < stepStates.length - 1 ? "1px solid var(--color-border)" : "none" }}>
                {step.status === "done" ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-500 shrink-0">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                ) : (
                  <span className="w-3.5 h-3.5 rounded-full shrink-0 border border-[var(--color-border)] bg-transparent" />
                )}
                <span className={`flex-1 text-xs ${step.status === "pending" ? "text-[var(--color-fg-tertiary)]" : "text-[var(--color-fg)]"}`}>
                  {step.label}
                </span>
              </div>
            ))}
          </div>

          {/* Report file */}
          {data.report && (
            <div className="mx-3.5 mt-2.5 mb-2.5 px-2.5 py-2 rounded-lg bg-[var(--color-border)]/30 border border-[var(--color-border)] flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-md bg-[var(--color-accent)] flex items-center justify-center shrink-0">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-[var(--color-fg)] truncate">{data.report.filename}</div>
                <div className="text-[10px] text-[var(--color-fg-tertiary)]" style={{ fontFamily: "var(--font-mono)" }}>{data.report.filesize} &middot; PDF</div>
              </div>
              {data.report.status === "generating" ? (
                <div className="flex items-center gap-1.5 shrink-0">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--color-fg-secondary)] animate-spin"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                  <span className="text-[10px] font-medium text-[var(--color-fg-secondary)]" style={{ fontFamily: "var(--font-mono)" }}>Generating...</span>
                </div>
              ) : getReportDownloadUrl(data.report) ? (
                <button
                  type="button"
                  onClick={() => { void handleDownload(); }}
                  disabled={downloading}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-[var(--color-fg)] text-[var(--color-bg)] text-[11px] font-medium cursor-pointer"
                  style={{ fontFamily: "var(--font-mono)" }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                  {downloading ? "Downloading..." : "Download"}
                </button>
              ) : (
                <button disabled className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-[var(--color-fg)]/40 text-[var(--color-bg)] text-[11px] font-medium" style={{ fontFamily: "var(--font-mono)" }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                  Download
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
